/**
 * SectionAssociations — the "link to the sections above" control shown inside
 * each multi-type section below the first.
 *
 * For a given section (the target / "below" row), it renders one row per
 * section above it (each a potential source / "above" row): the row above is
 * always the association SOURCE and this row is the TARGET. Each row offers a
 * dropdown of the valid association types for that directed pair — computed
 * kind-aware via the matchers — defaulting to the first valid type, with a
 * "Don't link" option. When a pair has no valid association type, a warning is
 * shown instead of a dropdown.
 */

import { AlertTriangle, Link01 } from '@untitledui/icons';
import type { Key } from 'react';
import { useCallback } from 'react';

import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { AssociationMatcher } from '@/lib/crm/hooks/useAssociationMatchers';

import {
  ASSOCIATION_DISABLED,
  directedPairKey,
  validAssociationTypesForPair,
} from '../../csv-import-helpers';

const DONT_LINK_ITEM = { label: "Don't link", id: ASSOCIATION_DISABLED };

interface AboveType {
  name: string;
  label: string;
}

interface SectionAssociationsProps {
  fileId: string | number;
  /** This section's entity type (the association target / "below" row). */
  targetType: string;
  targetLabel: string;
  /** Sections rendered above this one (each a potential source / "above" row). */
  aboveTypes: AboveType[];
  associationTypes: AssociationType[];
  matchesSource: AssociationMatcher;
  matchesTarget: AssociationMatcher;
  /** This file's selections: `${sourceType}|${targetType}` → assoc type ('' = off). */
  fileSelections: Record<string, string>;
  onChange: (
    fileId: string | number,
    sourceType: string,
    targetType: string,
    associationTypeName: string
  ) => void;
}

/** A single source→target association dropdown row. Extracted so hooks
 * (useCallback, useMemo) can be used per-row instead of inside .map(). */
function AssociationPairRow({
  source,
  targetType,
  targetLabel,
  fileId,
  selectValue,
  items,
  onChange,
}: {
  source: AboveType;
  targetType: string;
  targetLabel: string;
  fileId: string | number;
  selectValue: string;
  items: { id: string; label: string }[];
  onChange: SectionAssociationsProps['onChange'];
}) {
  const handleChange = useCallback(
    (key: Key | null) => {
      if (key != null) {
        onChange(fileId, source.name, targetType, String(key));
      }
    },
    [onChange, fileId, source.name, targetType]
  );

  return (
    <div className='flex items-center justify-between gap-3'>
      <span className='min-w-0 truncate text-sm text-fg-secondary'>
        {source.label}
      </span>
      <Select
        size='xs'
        className='w-auto shrink-0'
        aria-label={`Association type from ${source.label} to ${targetLabel}`}
        value={selectValue}
        onChange={handleChange}
        items={items}
      >
        {item => <SelectItem {...item} />}
      </Select>
    </div>
  );
}

export function SectionAssociations({
  fileId,
  targetType,
  targetLabel,
  aboveTypes,
  associationTypes,
  matchesSource,
  matchesTarget,
  fileSelections,
  onChange,
}: SectionAssociationsProps) {
  if (aboveTypes.length === 0) {
    return null;
  }

  return (
    <div className='rounded-lg border border-border-secondary bg-bg-secondary p-3'>
      <div className='mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-fg-quaternary'>
        <Link01 className='size-3.5' aria-hidden='true' />
        Link {targetLabel} to
      </div>
      <div className='flex flex-col gap-2'>
        {aboveTypes.map(source => {
          const validTypes = validAssociationTypesForPair(
            associationTypes,
            matchesSource,
            matchesTarget,
            source.name,
            targetType
          );
          const key = directedPairKey(source.name, targetType);

          if (validTypes.length === 0) {
            return (
              <div
                key={source.name}
                className='flex items-center gap-2 text-sm text-fg-tertiary'
              >
                <AlertTriangle
                  className='size-4 shrink-0 text-fg-warning-primary'
                  aria-hidden='true'
                />
                <span>
                  No valid association from {source.label} to {targetLabel}.
                </span>
              </div>
            );
          }

          const stored = fileSelections[key];
          const selectValue =
            stored !== undefined
              ? stored
              : (validTypes[0]?.association_type_name ?? ASSOCIATION_DISABLED);
          const items = [
            DONT_LINK_ITEM,
            ...validTypes.map(at => ({
              label: at.label,
              id: at.association_type_name,
            })),
          ];

          return (
            <AssociationPairRow
              key={source.name}
              source={source}
              targetType={targetType}
              targetLabel={targetLabel}
              fileId={fileId}
              selectValue={selectValue}
              items={items}
              onChange={onChange}
            />
          );
        })}
      </div>
    </div>
  );
}
